import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Database, Download, Upload, Loader2, AlertTriangle, HardDrive, Play } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDatabase() {
  const { authFetch, getAccessTokenSilently } = useAdmin();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [wipeFirst, setWipeFirst] = useState(false);
  const [serverBackups, setServerBackups] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/database/collections`);
    if (r.ok) setCollections((await r.json()).collections || []);
    try {
      const rb = await authFetch(`${API}/admin/database/backups`);
      if (rb.ok) setServerBackups((await rb.json()).backups || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const exportNow = async () => {
    setBusy('export');
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/admin/database/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error('Export neuspješan'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `e-mina-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export preuzet');
    } catch { toast.error('Export neuspješan'); }
    finally { setBusy(''); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('import');
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const payload = data.collections ? data.collections : data;
      const r = await authFetch(`${API}/admin/database/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wipe_first: wipeFirst, data: payload }),
      });
      if (r.ok) {
        const res = await r.json();
        toast.success(`Uvezeno ${res.total} zapisa`);
        load();
      } else toast.error('Import neuspješan');
    } catch (err) { toast.error(`JSON neispravan: ${err.message}`); }
    finally { setBusy(''); e.target.value = ''; }
  };

  const backupNow = async () => {
    setBusy('backup');
    try {
      const r = await authFetch(`${API}/admin/database/backup-now`, { method: 'POST' });
      if (r.ok) {
        const res = await r.json();
        toast.success(`Backup spremljen: ${res.filename} (${res.docs} zapisa)`);
        load();
      } else toast.error('Backup neuspješan');
    } catch { toast.error('Backup neuspješan'); }
    finally { setBusy(''); }
  };

  const downloadServerBackup = async (filename) => {
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/admin/database/backups/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error('Preuzimanje neuspješno'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Preuzimanje neuspješno'); }
  };

  const fmtSize = (b) => {
    if (b > 1_048_576) return `${(b / 1_048_576).toFixed(1)} MB`;
    if (b > 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${b} B`;
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="admin-database">
      <div className="flex items-center gap-3 mb-5">
        <Database className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Baza podataka</h1>
          <p className="text-sm text-zinc-400">Puni export (JSON) i uvoz svih admin kolekcija. <code>users</code> se ne prepisuje.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/50">
          <h2 className="font-bold flex items-center gap-2"><Download size={16} /> Export</h2>
          <p className="text-xs text-zinc-400 mt-1 mb-3">Preuzmi cijelu bazu (svi članci, kategorije, postavke…) kao JSON datoteku.</p>
          <button onClick={exportNow} disabled={busy === 'export'} data-testid="db-export-btn"
                  className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm font-bold disabled:opacity-50">
            {busy === 'export' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export kao JSON
          </button>
        </div>

        <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/50">
          <h2 className="font-bold flex items-center gap-2"><Upload size={16} /> Import</h2>
          <p className="text-xs text-zinc-400 mt-1 mb-3">Učitaj export JSON datoteku. Svaki dokument se radi upsert po <code>id</code>.</p>
          <label className="flex items-start gap-2 mb-3 cursor-pointer text-xs">
            <input type="checkbox" checked={wipeFirst} onChange={e => setWipeFirst(e.target.checked)} data-testid="db-wipe-check" />
            <span className="text-amber-400"><AlertTriangle size={12} className="inline mr-1" />Obriši postojeće prije uvoza (<b>oprez</b>)</span>
          </label>
          <label className="inline-flex items-center gap-1 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded text-sm font-bold cursor-pointer">
            {busy === 'import' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Odaberi JSON…
            <input type="file" accept="application/json" onChange={importFile} className="hidden" data-testid="db-import-input" />
          </label>
        </div>
      </div>

      <h2 className="font-bold mb-2 text-sm uppercase tracking-wider text-zinc-400">Serverski backup (otporan na timeout)</h2>
      <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/50 mb-6" data-testid="server-backups">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-bold flex items-center gap-2"><HardDrive size={16} /> Pohranjene na serveru</h3>
            <p className="text-xs text-zinc-400 mt-1">Automatski dnevni backup u 03:00 (Europe/Zagreb). Čuva se 30 dana. Koristi ovu opciju kad streaming export padne zbog timeouta.</p>
          </div>
          <button onClick={backupNow} disabled={busy === 'backup'} data-testid="db-backup-now-btn"
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-sm font-bold disabled:opacity-50">
            {busy === 'backup' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Backup sada
          </button>
        </div>
        {serverBackups.length === 0 ? (
          <p className="text-xs text-zinc-500 italic">Još nema spremljenih backup datoteka.</p>
        ) : (
          <ul className="divide-y divide-zinc-800 text-sm">
            {serverBackups.map(b => (
              <li key={b.filename} className="py-2 flex items-center justify-between gap-2" data-testid={`server-backup-${b.filename}`}>
                <div>
                  <div className="font-mono text-xs">{b.filename}</div>
                  <div className="text-[11px] text-zinc-500">{fmtSize(b.size_bytes)} · {new Date(b.modified).toLocaleString('hr-HR')}</div>
                </div>
                <button onClick={() => downloadServerBackup(b.filename)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs">
                  <Download size={11} /> Preuzmi
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <h2 className="font-bold mb-2 text-sm uppercase tracking-wider text-zinc-400">Kolekcije</h2>
      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900 text-xs uppercase text-zinc-400">
              <tr>
                <th className="px-3 py-2 text-left">Naziv</th>
                <th className="px-3 py-2 text-right">Dokumenti</th>
                <th className="px-3 py-2 text-left">U exportu?</th>
              </tr>
            </thead>
            <tbody>
              {collections.map(c => (
                <tr key={c.name} className="border-t border-zinc-800" data-testid={`col-${c.name}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{c.name}</td>
                  <td className="px-3 py-1.5 text-right">{c.count.toLocaleString()}</td>
                  <td className="px-3 py-1.5">{c.exportable ? <span className="text-emerald-400">Da</span> : <span className="text-zinc-500">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
